import { BoxProps, Divider, Stack, Typography, Box, styled, Skeleton } from '@mui/material';
import { motion } from 'framer-motion';
import { ReactElement } from 'react';
import { useIsMobile } from '../hooks';
import { threeDIcon } from '../config/icons';

// Use Box instead of styled div to avoid type issues
export const ActionsContainer: React.ComponentType<any> = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
  borderTop: '1px solid #dfdfdf',
  alignItems: 'center',
  padding: '14px',
  gap: '10px',
}));

export const InfoCardBadge: React.ComponentType<any> = styled(Box)(() => ({
  display: 'flex',
  height: '27px',
  padding: '0px 10px',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '5.513px',
  borderRadius: '88.204px',
  background: '#fff',
}));

// Define badge styles
export const Badge: React.ComponentType<any> = styled(Box)(
  ({ variant }: { variant: 'success' | 'info' }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '0 9px',
    borderRadius: '90px',
    fontWeight: 500,
    fontSize: '17px',
    lineHeight: '2px',
    letterSpacing: '0.2px',
    whiteSpace: 'nowrap',
    height: '28px',
    backgroundColor: variant === 'success' ? '#e3ffd6' : 'rgba(4, 148, 254, 0.17)',
    color: variant === 'success' ? '#27ae60' : '#0494fe',
  }),
);

export type InfoCardProps = BoxProps & {
  animate?: boolean;
  asButton?: boolean;
  isFullWidth?: boolean;
  infoContent?: ReactElement;
  infoContentDivider?: boolean;
  buildingLabel?: string;
  showSuccessBadge?: boolean;
  show3DBadge?: boolean;
  showPromotionalLabel?: string;
  showImageComponent?: boolean;
  title?: string;
  description?: string;
  imageSrc?: string;
  imageHeight?: number;
  icon?: ReactElement;
  onClick?: () => void;
  onClose?: () => void;
  actions?: ReactElement;
  availableLabel?: string;
};

const variants = {
  hidden: { opacity: 0, scale: 0 },
  visible: { opacity: 1, scale: 1 },
};

export function InfoCard({
  infoContent,
  infoContentDivider = true,
  showImageComponent = true,
  showSuccessBadge,
  show3DBadge,
  showPromotionalLabel,
  buildingLabel,
  actions,
  imageSrc,
  title,
  description,
  isFullWidth,
  animate,
  asButton,
  sx,
  onClick,
  onClose,
  imageHeight = 160,
  icon,
  availableLabel,
  ...rest
}: InfoCardProps) {
  const isMobile = useIsMobile();

  return (
    <Stack
      component={motion.div}
      initial="hidden"
      animate="visible"
      position="relative"
      variants={isMobile ? undefined : variants}
      transition={{ duration: animate ? 0.5 : 0 }}
      onClick={onClick}
      {...rest}
      sx={{
        borderRadius: '12px',
        border: '1px solid #e4e7ec',
        backgroundColor: '#fff',
        overflow: 'hidden',
        height: '100%',
        cursor: asButton ? 'pointer' : 'default',
        ...sx,

        ...(isFullWidth && {
          borderRadius: '14px 14px 0 0',
          border: 'none',
          boxShadow: 'none',
          maxWidth: '100%',
        }),
      }}
    >
      <Stack
        direction="row"
        sx={{
          position: 'absolute',
          top: 10,
          left: 10,
        }}
        spacing={1}
      >
        {showPromotionalLabel && (
          <InfoCardBadge>
            <Typography
              fontSize={12}
              fontWeight={600}
              color="#0494FE"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: '1',
                WebkitBoxOrient: 'vertical',
              }}
            >
              {showPromotionalLabel}
            </Typography>
          </InfoCardBadge>
        )}
        {show3DBadge && imageSrc && <InfoCardBadge>{threeDIcon}</InfoCardBadge>}
      </Stack>
      {showImageComponent && (
        <>
          {imageSrc ? (
            <img
              src={imageSrc}
              width={300}
              height={imageHeight}
              style={{
                width: '100%',
                maxHeight: `${imageHeight}px`,
                objectFit: 'cover',
                pointerEvents: 'none',
              }}
              alt="gallery image"
            />
          ) : (
            <Skeleton
              variant="rectangular"
              width="100%"
              height={imageHeight}
              animation={false}
              sx={{ borderRadius: '12px 12px 0 0' }}
            />
          )}
        </>
      )}
      <Stack p={2} spacing={1} flexGrow={1}>
        {buildingLabel && (
          <Stack direction="row" width="100%" justifyContent="space-between">
            <Stack direction="row" alignItems="center" spacing={1} width="100%">
              {icon}
              <Typography
                color="grey.600"
                variant="body2"
                sx={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {buildingLabel}
              </Typography>
            </Stack>
            <Stack direction="row" alignItems="center" spacing={1}>
              {!imageSrc && show3DBadge && <InfoCardBadge>{threeDIcon}</InfoCardBadge>}
              {showSuccessBadge ? <Badge variant="success">{availableLabel}</Badge> : <div />}
            </Stack>
          </Stack>
        )}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {title}
        </Typography>
        {description && (
          <Typography fontSize="14px" lineHeight="20px">
            {description}
          </Typography>
        )}

        {infoContent && (
          <>
            {infoContentDivider && (
              <Divider
                sx={{
                  mb: 0.5,
                }}
              />
            )}

            <Stack direction="row" flexWrap="wrap" spacing={1.5} width="80%">
              {infoContent}
            </Stack>
          </>
        )}
      </Stack>

      {actions && <ActionsContainer>{actions}</ActionsContainer>}
    </Stack>
  );
}
